import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Rocket, ArrowRight, Loader2, Sparkles, CheckCircle2, Clock,
  Target, ShieldAlert, Gauge,
} from "lucide-react";
import { goPacks, goCreateBlueprint } from "@/lib/api";
import { toast } from "sonner";

/** Growth Blueprint intake — a compact, conversational form.
 *  Only 12 questions in Phase 1 to match the master spec §5 without a wall. */

const LEAD_BUCKETS = ["<10", "10-50", "50-200", "200+"];
const URGENCY = ["exploring", "next-30-days", "urgent"];
const BUDGETS = ["<$500/mo", "$500-$1500/mo", "$1500-$5000/mo", "$5000+/mo"];
const GOAL_OPTS = [
  "More qualified leads",
  "Faster response times",
  "Consistent content & social",
  "Better reviews & reputation",
  "Fewer missed calls",
  "Automated follow-up",
  "One customer record",
  "Multilingual customer service",
];

const readinessTone = (v) => v >= 75 ? "emerald" : v >= 50 ? "amber" : "rose";
const READ_TONE = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
};

const Field = ({ label, hint, children, required }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
      {label}{required && <span className="ml-1 text-rose-500">*</span>}
    </label>
    {children}
    {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
  </div>
);

export default function GrowthOperatorBlueprint() {
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", contact_email: "", contact_phone: "",
    website: "", industry_pack: "", monthly_leads: "10-50",
    biggest_bottleneck: "", goals: [], languages: ["en"],
    budget_range: "", urgency: "next-30-days", wants_human: false, consent_marketing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { goPacks().then(r => setPacks(r.packs || [])); }, []);

  const toggleGoal = (g) => {
    setForm(f => ({
      ...f,
      goals: f.goals.includes(g) ? f.goals.filter(x => x !== g) : [...f.goals, g].slice(0, 8),
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.business_name.trim() || !form.contact_name.trim()
        || !form.contact_email.trim() || !form.industry_pack
        || !form.biggest_bottleneck.trim()) {
      toast.error("Please fill the starred fields");
      return;
    }
    setSubmitting(true);
    try {
      const r = await goCreateBlueprint({
        ...form,
        website: form.website?.trim() || undefined,
      });
      setResult(r);
      // scroll to results
      setTimeout(() => document.getElementById("bp-report")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Blueprint failed to generate.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="go-blueprint">
      <Navbar />

      <section className="border-b border-slate-200 bg-gradient-to-br from-orange-50 via-white to-sky-50">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-orange-700 backdrop-blur">
            <Rocket className="h-3.5 w-3.5" /> Free Growth Blueprint
          </div>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-slate-900 sm:text-5xl">
            Get a personalised 30/60/90-day growth plan.
          </h1>
          <p className="mt-4 max-w-2xl text-slate-700">
            About 3 minutes. We combine your intake with a live site audit and
            recommend the right service tier, agents and quick wins. No payment.
          </p>
        </div>
      </section>

      {!result && (
        <form onSubmit={submit} className="mx-auto max-w-4xl px-4 py-10 space-y-8" data-testid="go-blueprint-form">

          {/* Contact */}
          <Card className="p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">1. About you</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Business name" required>
                <Input data-testid="bp-business_name" value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} placeholder="Northside Dental Clinic" />
              </Field>
              <Field label="Your name" required>
                <Input data-testid="bp-contact_name" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Dr Jane Kim" />
              </Field>
              <Field label="Email" required>
                <Input data-testid="bp-contact_email" type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="jane@example.com" />
              </Field>
              <Field label="Phone">
                <Input data-testid="bp-contact_phone" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="Optional" />
              </Field>
              <Field label="Website URL" hint="If you have one — we'll run a live audit as part of your report.">
                <Input data-testid="bp-website" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://your-site.com" />
              </Field>
            </div>
          </Card>

          {/* Industry pack */}
          <Card className="p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">2. Pick your industry pack</h2>
            <div className="mt-2 text-sm text-slate-500">Each pack pre-loads the right agents, KPIs and workflows.</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {packs.map(p => {
                const on = form.industry_pack === p.slug;
                return (
                  <button
                    type="button"
                    key={p.slug}
                    onClick={() => setForm({ ...form, industry_pack: p.slug })}
                    className={`rounded-xl border p-4 text-left transition ${on ? "border-orange-500 bg-orange-50 ring-1 ring-orange-300" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`bp-pack-${p.slug}`}
                  >
                    <div className="font-heading text-sm font-bold text-slate-900">{p.name}</div>
                    <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{p.problem}</div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Bottleneck + goals */}
          <Card className="p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">3. What's the biggest bottleneck right now?</h2>
            <Textarea
              data-testid="bp-biggest_bottleneck"
              className="mt-3 min-h-[110px]"
              value={form.biggest_bottleneck}
              onChange={e => setForm({ ...form, biggest_bottleneck: e.target.value })}
              placeholder="e.g. Missed calls after hours, review response, empty hygiene chair, slow quotes..."
              required
            />
            <div className="mt-6 font-heading text-sm font-bold text-slate-900">Goals (pick up to 5)</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {GOAL_OPTS.map(g => (
                <button
                  type="button"
                  key={g}
                  onClick={() => toggleGoal(g)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${form.goals.includes(g) ? "border-orange-500 bg-orange-500 text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                  data-testid={`bp-goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </Card>

          {/* Volume + budget + urgency */}
          <Card className="p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">4. Volume & timing</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Field label="Monthly lead volume" required>
                <select
                  data-testid="bp-monthly_leads"
                  value={form.monthly_leads}
                  onChange={e => setForm({ ...form, monthly_leads: e.target.value })}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  {LEAD_BUCKETS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Budget range">
                <select
                  data-testid="bp-budget_range"
                  value={form.budget_range}
                  onChange={e => setForm({ ...form, budget_range: e.target.value })}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Prefer not to say</option>
                  {BUDGETS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Urgency">
                <select
                  data-testid="bp-urgency"
                  value={form.urgency}
                  onChange={e => setForm({ ...form, urgency: e.target.value })}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  {URGENCY.map(v => <option key={v} value={v}>{v.replace(/-/g, " ")}</option>)}
                </select>
              </Field>
            </div>
            <div className="mt-4 flex items-start gap-2">
              <Checkbox
                id="bp-wants_human"
                checked={form.wants_human}
                onCheckedChange={(v) => setForm({ ...form, wants_human: !!v })}
                data-testid="bp-wants_human"
              />
              <label htmlFor="bp-wants_human" className="text-sm text-slate-700">
                I'd like a dedicated human Growth Operator (not just AI).
              </label>
            </div>
            <div className="mt-2 flex items-start gap-2">
              <Checkbox
                id="bp-consent"
                checked={form.consent_marketing}
                onCheckedChange={(v) => setForm({ ...form, consent_marketing: !!v })}
                data-testid="bp-consent"
              />
              <label htmlFor="bp-consent" className="text-sm text-slate-700">
                WebsiteTech may contact me about my Blueprint by email or SMS. I can opt out any time.
              </label>
            </div>
          </Card>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              We never share your data. Your Blueprint is private to your business.
            </div>
            <Button type="submit" size="lg" disabled={submitting} className="h-12 bg-orange-500 px-8 text-base font-bold text-white hover:bg-orange-400" data-testid="bp-submit">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Generate my Growth Blueprint
            </Button>
          </div>
        </form>
      )}

      {result && <BlueprintReport data={result} onReset={() => { setResult(null); window.scrollTo({ top: 0 }); }} />}

      <Footer />
    </div>
  );
}


function BlueprintReport({ data, onReset }) {
  const tone = readinessTone(data.readiness);
  const rTone = READ_TONE[tone];
  const plan = data.plan || {};
  const audit = data.site_audit;

  return (
    <section id="bp-report" className="mx-auto max-w-5xl px-4 py-14 space-y-8" data-testid="bp-report">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-orange-600">Your Growth Blueprint</div>
            <h2 className="mt-1 font-heading text-3xl font-black text-slate-900">{plan.headline || "Your 30/60/90-day plan"}</h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-700">{plan.executive_summary}</p>
          </div>
          <div className={`shrink-0 rounded-2xl border p-5 ${rTone}`} data-testid="bp-readiness">
            <div className="text-xs font-semibold uppercase tracking-wider opacity-70">Readiness</div>
            <div className="font-heading text-4xl font-black tabular-nums">{data.readiness}<span className="text-lg opacity-60">/100</span></div>
          </div>
        </div>
      </div>

      <Card className="p-6" data-testid="bp-tier">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange-600">
          <Target className="h-4 w-4" /> Recommended tier
        </div>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-black text-slate-900">{data.recommended_tier.name}</div>
            <div className="text-sm text-slate-600">{data.recommended_tier.tagline}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-xl font-black text-orange-600">{data.recommended_tier.price_display}</div>
            <div className="text-xs text-slate-500">{data.recommended_tier.best_for}</div>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {data.recommended_tier.includes.map((inc, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{inc}
            </div>
          ))}
        </div>
      </Card>

      {plan.quick_wins?.length > 0 && (
        <Card className="p-6" data-testid="bp-quick-wins">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-600">
            <Sparkles className="h-4 w-4" /> Quick wins (low effort)
          </div>
          <ul className="mt-3 space-y-3">
            {plan.quick_wins.map((q, i) => (
              <li key={i} className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="font-heading text-sm font-bold text-emerald-900">{q.title}</div>
                <div className="mt-1 text-xs text-emerald-900/80">{q.why}</div>
                {q.effort && <Badge variant="outline" className="mt-2 text-[10px] capitalize">Effort: {q.effort}</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {["day_30", "day_60", "day_90"].map((k, idx) => (
        plan[k]?.length > 0 && (
          <Card key={k} className="p-6" data-testid={`bp-${k}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange-600">
              <Clock className="h-4 w-4" /> {["First 30 days", "Days 31-60", "Days 61-90"][idx]}
            </div>
            <ul className="mt-3 space-y-3">
              {plan[k].map((t, i) => (
                <li key={i} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-heading text-sm font-bold text-slate-900">{t.title}</div>
                    <Badge variant="outline" className="text-[10px]">{t.owner}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-slate-600">{t.deliverable}</div>
                  {t.estimated_hours_saved > 0 && (
                    <div className="mt-2 text-[11px] font-semibold text-emerald-700">
                      Est. {t.estimated_hours_saved} hrs saved / month (estimate)
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )
      ))}

      {plan.risks?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 p-6" data-testid="bp-risks">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
            <ShieldAlert className="h-4 w-4" /> Risks & limitations
          </div>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-900">
            {plan.risks.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </Card>
      )}

      {plan.measurement_plan?.length > 0 && (
        <Card className="p-6" data-testid="bp-measurement">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
            <Gauge className="h-4 w-4" /> Measurement plan
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {plan.measurement_plan.map((m, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3">
                <div className="font-heading text-sm font-bold text-slate-900">{m.kpi}</div>
                <div className="text-[11px] text-slate-500">Source: {m.source}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {audit && audit.reachable && (
        <Card className="p-6" data-testid="bp-audit">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live site audit</div>
          <div className="mt-2 text-sm text-slate-700 font-mono truncate">{audit.final_url}</div>
          <div className="mt-3 grid gap-2 sm:grid-cols-3 text-xs">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">HTTPS: <b>{audit.https ? "yes" : "no"}</b></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">Word count: <b>{audit.word_count}</b></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">Findings: <b>{audit.issue_count}</b></div>
          </div>
          {audit.top_issues?.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-slate-700">
              {audit.top_issues.map((i, idx) => (
                <li key={idx} className="rounded border border-slate-100 bg-white px-3 py-2">
                  <b>{i.code.replace(/_/g, " ")}</b> — {i.evidence}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={onReset} data-testid="bp-reset">← Edit answers</Button>
        <Button asChild size="lg" className="h-12 bg-orange-500 px-8 text-base font-bold text-white hover:bg-orange-400" data-testid="bp-book">
          <Link to="/contact">Book my 20-min walk-through <ArrowRight className="ml-1 h-4 w-4" /></Link>
        </Button>
      </div>
    </section>
  );
}
